import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Divider } from 'antd';
import { CustomerServiceStatus } from '@/utils/helpers';
import { getListCustomer } from '@/redux/actions/doctorCustomerActions';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import DynamicTable from '@/components/DynamicTable';
import useUrlFilters from '@/hooks/useUrlFilters';
import FilterableMultiSelect from '@/components/FilterableMultiSelect';
import FilterableMultiTreeSelect from '@/components/FilterableMultiTreeSelect';
import { buildCustomerColumns } from '@/columns/buildCustomerColumns';
import CustomerScheduleDetail from '@/routes/customers/CustomerScheduleDetail';
import { fetchFilters } from '@/redux/actions/filterActions';

const title = 'Danh sách bệnh nhân';
const path = 'doctor-customers';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: `/${path}`, breadcrumbName: title },
];


export default function Index() {

    const location = useLocation();
    const dispatch = useDispatch();

    const items = useSelector(state => state.doctorCustomerReducer.list.items);
    const pagination = useSelector(state => state.doctorCustomerReducer.list.pagination);

    const users = useSelector(state => state.config.users);
    const doctors = useSelector(state => state.config.doctors);
    const consultants = useSelector(state => state.config.consultants);

    const filterData = useSelector(state => state.filterReducer.list);

    const list_provinces = filterData?.provinces;
    const customer_source_active_list = filterData?.sources;
    const branchActiveList = filterData?.branches;
    const customerClassActiveList = filterData?.classes;
    const customer_group_list = filterData?.groups;
    const customerStatusActiveList = filterData?.statuses;
    const service_active_list = filterData?.services;

    useEffect(() => {
        dispatch(fetchFilters());
    }, [dispatch]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenScheduleDetail, setIsOpenScheduleDetail] = useState(false);
    const [currentData, setCurrentData] = useState(null);

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);

    const loadData = useCallback((q = query) => {
        setIsLoading(true);
        dispatch(getListCustomer(q)).finally(() => setIsLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadData(query);
    }, [query, loadData]);

    const { handleFilterChange } = useUrlFilters({
        initialExtra: { page: 1, per_page: 30 }, // tuỳ chọn
        rangeMap: {
            // Ngày tạo
            created_date: { from: 'created_date_from', to: 'created_date_to', type: 'date' },
            // Lịch hẹn
            appointment_schedule: { from: 'appointment_schedule_from', to: 'appointment_schedule_to', type: 'date' },
            // Ngày chốt
            usage_date: { from: 'usage_date_from', to: 'usage_date_to', type: 'date' },
            // Ngày sinh
            birthday: { from: 'birthday_from', to: 'birthday_to', type: 'month-day' },
            // Ngày cskh
            care_date: { from: 'care_date_from', to: 'care_date_to', type: 'date' },
        }
    });

    const openScheduleDetail = (record) => {
        setIsOpenScheduleDetail(true);
        setCurrentData(record);
    }

    const columns = useMemo(() => buildCustomerColumns(
        {
            onOpenScheduleDetail: (record) => openScheduleDetail(record),
            loadData: loadData,
        },
        {
            // Ghi đè thuộc tính cột:
            overrides: {
                full_name: { width: 300 },
            },
            hiddenKeys: ['revenue_summary', 'fb_name', 'phone', 'created_at', 'actions']
        }
    ), [openScheduleDetail]);

    const tables = <DynamicTable
        dataSource={items || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        rowSelection={false}
        moduleKey="doctor_customer_list"
    />

    return (
        <div>
            <PageTitle routes={routes} title={title} />
            <TableActionBar
                isShowAddButton={false}
                isShowDeleteButton={false}
                disabled={!selectedRowKeys.length}
                showSearch
                showFilter={true}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                filterColSpan={6}
                activeFilter={
                    query.branch_id ||
                    query.group_ids ||

                    query.appointment_schedule_from ||
                    query.usage_date_from ||
                    query.birthday_from ||
                    query.care_date_from ||

                    query.interested_service_id ||
                    query.selected_service_id ||
                    query.service_status ||

                    query.customer_class_id ||
                    query.customer_status_id ||

                    query.province_code ||

                    query.tele_staff ||
                    query.sales_staff ||

                    query.customer_source_id ||

                    query.doctor_charge ||
                    query.consultant_staff ||
                    query.care_staff_id
                }

                filters={
                    [
                        <FilterableMultiSelect
                            options={branchActiveList || []}
                            placeholder="Chọn cơ sở"
                            parameterName='branch_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customer_group_list || []}
                            placeholder="Chọn nhóm"
                            parameterName='group_ids'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <BaseRangeFilter
                            name="appointment_schedule"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Lịch hẹn', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            name="usage_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày chốt', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            type="month-day"
                            name="birthday"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Sinh nhật', 'Đến ngày']}
                        />,
                        <FilterableMultiTreeSelect
                            options={service_active_list || []}
                            placeholder="Dịch vụ quan tâm"
                            parameterName='interested_service_id'
                        />,
                        <FilterableMultiTreeSelect
                            options={service_active_list || []}
                            placeholder="Dịch vụ chốt"
                            parameterName='selected_service_id'
                        />,
                        <FilterableMultiSelect
                            options={CustomerServiceStatus || []}
                            placeholder="Trạng thái dịch vụ chốt"
                            parameterName='service_status'
                            optionValue="value"
                            optionLabel="label"
                        />,
                        <FilterableMultiSelect
                            options={customerClassActiveList || []}
                            placeholder="Hạng khách hàng"
                            parameterName='customer_class_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customerStatusActiveList || []}
                            placeholder="Trạng thái khách hàng"
                            parameterName='customer_status_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={list_provinces || []}
                            placeholder="Tỉnh/Thành phố"
                            parameterName='province_code'
                            optionValue="code"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={users || []}
                            placeholder="Nhân viên telesale"
                            parameterName='tele_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={users || []}
                            placeholder="Nhân viên chốt"
                            parameterName='sales_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={customer_source_active_list || []}
                            placeholder="Nguồn khách hàng"
                            parameterName='customer_source_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={doctors || []}
                            placeholder="Bác sĩ thực hiện"
                            parameterName='doctor_charge'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={consultants || []}
                            placeholder="Nhân viên tư vấn"
                            parameterName='consultant_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <BaseRangeFilter
                            name="care_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày CSKH', 'Đến ngày']}
                        />,
                        <FilterableMultiSelect
                            options={consultants || []}
                            placeholder="Nhân viên CSKH"
                            parameterName='care_staff_id'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />
                    ]
                }
            >
                <Button className='table-button' icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData(query)}>Refresh</Button>
                {selectedRowKeys.length > 0 && <span><Divider type="vertical" />Đang chọn {selectedRowKeys.length} bản ghi </span>}
            </TableActionBar>

            <div className='has-custom-column-table-btn'>
                {tables}
            </div>

            <CustomerScheduleDetail
                visible={isOpenScheduleDetail}
                currentItem={currentData}
                onCancel={() => setIsOpenScheduleDetail(false)}
            />
        </div>
    );
}
